import {getLanguage} from './i18n';
document.documentElement.lang=getLanguage();
// src/index.js
// Inicialización de la app: intentamos aplicar inMemoryPersistence *antes* de montar React
// y envolvemos la app con AuthProvider para que la UI acepte sesiones "manual" (fallback REST).

import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { registerDeepLinkHandler } from "./deeplinkHandler";
import { isIosCapacitor } from "./utils/nativeHttp";

// import helper to attempt inMemoryPersistence as early as possible
import { appCheckReady, ensureInMemoryPersistenceNow } from "./firebase";


function isNativeRuntimeNow() {
  try {
    if (typeof window === "undefined") return false;
    const cap = window.Capacitor;
    if (!cap) return false;
    if (typeof cap.isNativePlatform === "function") return cap.isNativePlatform();
    const platform = typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform || null;
    return !!(platform && platform !== "web");
  } catch (_) {
    return false;
  }
}


window.__GOOGLE_WEB_CLIENT_ID__ = window.__GOOGLE_WEB_CLIENT_ID__ || "357961918175-otucm5e15jcpjo7p3ht5e7nblv12ago8.apps.googleusercontent.com";

// DEFAULT: set the custom token exchange endpoint to your functions API.
window.__CUSTOM_TOKEN_EXCHANGE_URL__ =
  window.__CUSTOM_TOKEN_EXCHANGE_URL__ ||
  process.env.REACT_APP_CUSTOM_TOKEN_EXCHANGE_URL ||
  "https://us-central1-web-app-d9799.cloudfunctions.net/api";

(function markBodyIfMobileApp() {
  try {
    if (typeof document === "undefined" || typeof navigator === "undefined" || typeof window === "undefined") return;
    const ua = navigator.userAgent || "";
    const looksLikeWebView = /WebView|wv|Capacitor|Crosswalk|ReactNativeWebView/i.test(ua) || /; wv\)/i.test(ua);
    if (looksLikeWebView) {
      try { document.body.classList.add("is-mobile-app"); } catch (_) {}
    }
  } catch (e) {}
})();

setTimeout(() => {
  try {
    registerDeepLinkHandler((userCred) => {
      try { window.location.href = "/"; } catch (_) {}
    });
  } catch (e) {
    console.warn("[index] registerDeepLinkHandler failed:", e);
  }
}, 0);

async function ensurePersistenceThenMount() {
  // LOOP_APP_CHECK_STARTUP_V1
  // Wait briefly for native App Check initialization before mounting
  // components that can start Firebase reads. Enforcement is still disabled,
  // so a timeout or attestation error must never prevent the app from loading.
  try {
    const appCheckStartup = await Promise.race([
      appCheckReady,
      new Promise((resolve) =>
        setTimeout(
          () => resolve({ enabled: true, timedOut: true }),
          8000
        )
      ),
    ]);

    try {
      window.__LOOP_APP_CHECK_STARTUP__ = appCheckStartup;
    } catch (_) {}

    if (appCheckStartup?.error) {
      console.warn("[index] App Check startup reported an error:", appCheckStartup.error);
    } else if (appCheckStartup?.timedOut) {
      console.warn("[index] App Check startup timed out; mounting without enforcement");
    } else if (appCheckStartup?.enabled) {
      console.info("[index] App Check startup ready", appCheckStartup);
    }
  } catch (error) {
    console.warn(
      "[index] App Check startup failed; mounting without enforcement:",
      error?.message || error
    );
  }
  if (!isNativeRuntimeNow()) {
    mountApp();
    return;
  }

  if (!isIosCapacitor()) {
    try {
      console.debug("[index] Android/native runtime detected -> mount immediately");
    } catch (_) {}
    mountApp();
    return;
  }

  let mounted = false;
  const safeMount = () => {
    if (mounted) return;
    mounted = true;
    mountApp();
  };

  const hardTimeout = setTimeout(() => {
    try {
      console.warn("[index] iOS startup hard-timeout -> mount anyway");
    } catch (_) {}
    safeMount();
  }, 2200);

  try {
    try {
      window.__INMEM_TRY__ = window.__INMEM_TRY__ || {};
      window.__INMEM_TRY__.startedAt = new Date().toISOString();
    } catch (_) {}

    const res = await ensureInMemoryPersistenceNow(1500);

    try {
      window.__INMEM_TRY__.finishedAt = new Date().toISOString();
      window.__INMEM_TRY__.result = res;
    } catch (_) {}

    if (res && res.ok) {
      console.debug("[index] inMemoryPersistence applied before mount (iOS only)");
    } else {
      console.warn("[index] inMemoryPersistence not applied before mount (iOS only):", res && res.err ? res.err : res);
    }
  } catch (e) {
    console.warn("[index] ensureInMemoryPersistenceNow() threw:", e && e.message ? e.message : e);
  } finally {
    clearTimeout(hardTimeout);
    safeMount();
  }
}

function mountApp() {
  const container = document.getElementById("root");
  if (!container) {
    console.error("[index] No se encontró elemento #root para montar la app");
    return;
  }
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// Start: attempt persistence then mount (non-blocking if attempt fails)
ensurePersistenceThenMount();
